import React, { useRef, useState } from 'react';
import { Mail, Check, AlertCircle, Send } from '../lib/icons';
import { useNewsletterSubscription } from '../hooks/useNewsletter';

const NewsletterSignup: React.FC = () => {
  const [email, setEmail] = useState('');
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  // Honeypot: für Menschen unsichtbar, Bots füllen jedes Feld aus, das sie finden.
  const [website, setWebsite] = useState('');
  // Zeitpunkt des ersten Renderings — Grundlage der Ausfüll-Dauer-Prüfung.
  const formLoadedAt = useRef(Date.now());
  const { subscribe, loading, error, success } = useNewsletterSubscription();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (email.trim() && privacyAccepted) {
      await subscribe(email.trim(), website, formLoadedAt.current);
      if (!error) {
        setEmail('');
        setPrivacyAccepted(false);
      }
    }
  };

  return (
    <section className="bg-gradient-to-r from-blue-600 to-blue-700 text-white py-16">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <div className="mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-white bg-opacity-20 rounded-full mb-6">
            <Mail className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4">
            Bleiben Sie informiert
          </h2>
          <p className="text-lg sm:text-xl text-blue-100 max-w-2xl mx-auto">
            Erhalten Sie die neuesten Gesundheitstipps, neue medizinische Erkenntnisse und 
            Updates zu unserem Verzeichnis direkt in Ihr Postfach.
          </p>
        </div>

        {success ? (
          <div className="bg-green-500 bg-opacity-20 border border-green-300 rounded-lg p-6 max-w-md mx-auto">
            <div className="flex items-center justify-center mb-4">
              <Check className="w-8 h-8 text-green-200" />
            </div>
            <p className="text-xl font-semibold mb-2">Erfolgreich angemeldet!</p>
            <p className="text-green-100">
              Vielen Dank für Ihre Anmeldung. Sie erhalten in Kürze eine Bestätigungs-E-Mail.
            </p>
          </div>
        ) : (
          <div className="max-w-md mx-auto">
            <form onSubmit={handleSubmit} className="relative space-y-4">
              {/*
                Honeypot. Nicht per display:none versteckt — manche Bots
                erkennen das und überspringen solche Felder. Stattdessen aus
                dem sichtbaren Bereich geschoben, für Screenreader und
                Tab-Reihenfolge ausgenommen.
              */}
              <div className="absolute left-[-9999px] top-0 h-0 w-0 overflow-hidden" aria-hidden="true">
                <label htmlFor="newsletter-website">Website (bitte leer lassen)</label>
                <input
                  type="text"
                  id="newsletter-website"
                  name="website"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                  tabIndex={-1}
                  autoComplete="off"
                />
              </div>

              <div className="space-y-2">
                <div className="relative">
                  <Mail className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 transform text-gray-400 w-5 h-5" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Ihre E-Mail-Adresse"
                    className="w-full pl-12 pr-4 py-3 bg-white text-gray-900 rounded-lg focus:ring-2 focus:ring-blue-300 focus:outline-none text-base leading-none"
                    required
                  />
                </div>
              </div>

              <div className="flex items-start text-left">
                <input
                  type="checkbox"
                  id="privacy-consent"
                  checked={privacyAccepted}
                  onChange={(e) => setPrivacyAccepted(e.target.checked)}
                  className="mt-1 w-4 h-4 text-blue-600 bg-white border-gray-300 rounded focus:ring-2 focus:ring-blue-300"
                  required
                />
                <label htmlFor="privacy-consent" className="ml-3 text-sm text-white">
                  Ich akzeptiere die{' '}
                  <a
                    href="/datenschutz"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline hover:text-blue-200 transition-colors"
                  >
                    Datenschutzbestimmungen
                  </a>
                </label>
              </div>

              {error && (
                <div className="flex items-center text-red-200 text-sm bg-red-500 bg-opacity-20 rounded-lg p-3">
                  <AlertCircle className="w-4 h-4 mr-2 flex-shrink-0" />
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading || !email.trim() || !privacyAccepted}
                className="w-full bg-white text-blue-600 px-6 py-3 rounded-lg font-semibold hover:bg-blue-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 mr-2"></div>
                    Wird angemeldet...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5 mr-2" />
                    Newsletter abonnieren
                  </>
                )}
              </button>
            </form>
          </div>
        )}

        <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-6 text-center">
          <div className="bg-white bg-opacity-10 rounded-lg p-4">
            <h3 className="font-semibold mb-2">Gesundheitstipps</h3>
            <p className="text-blue-100 text-sm">
              Praktische Ratschläge für Ihren Alltag
            </p>
          </div>
          <div className="bg-white bg-opacity-10 rounded-lg p-4">
            <h3 className="font-semibold mb-2">Studien & Erkenntnisse</h3>
            <p className="text-blue-100 text-sm">
              Aktuelle medizinische Erkenntnisse und Studien
            </p>
          </div>
          <div className="bg-white bg-opacity-10 rounded-lg p-4">
            <h3 className="font-semibold mb-2">News</h3>
            <p className="text-blue-100 text-sm">
              Aktuelle News zum Thema Gesundheit & Behandlung.de
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default NewsletterSignup;